// ApiPlayground.tsx
// AI-powered API Business Case Explorer - combine APIs to discover insights

import { useState, useEffect } from 'react';
import { Bot, Zap, Lightbulb, TrendingUp, DollarSign, Code, AlertCircle } from 'lucide-react';
import { API_SOURCES_CONFIG } from '../config/apiSourcesConfig';
import { ApiSelection, GeminiModel, BusinessCase, EndpointCard } from '../types/apiPlayground';
import { useDemoMode } from '../contexts/DemoModeContext';
import { authenticatedFetch } from '../utils/apiClient';
import { CHART_PALETTE } from '../config/colors';

const GEMINI_MODELS: Array<{ value: GeminiModel; label: string }> = [
  { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
  { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' },
  { value: 'gemini-1.5-flash-8b', label: 'Gemini 1.5 Flash-8B' },
];

const API_COLORS: Record<string, string> = {
  aquarius: CHART_PALETTE[1],
  jigsaw: CHART_PALETTE[6],
  optifleet: CHART_PALETTE[0],
  qargo: CHART_PALETTE[2],
};

export default function ApiPlayground() {
  const [selectedA, setSelectedA] = useState<ApiSelection | null>(null);
  const [selectedB, setSelectedB] = useState<ApiSelection | null>(null);
  const [geminiModel, setGeminiModel] = useState<GeminiModel>('gemini-2.0-flash');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<BusinessCase | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showMatchBanner, setShowMatchBanner] = useState(false);
  const [endpoints, setEndpoints] = useState<EndpointCard[]>([]);
  const { isDemoMode } = useDemoMode();

  const DEMO_API_LABELS: Record<string, string> = {
    aquarius: 'API 1',
    jigsaw: 'API 2',
    optifleet: 'API 3',
    qargo: 'API 4',
  };

  const getApiDisplayName = (apiId: string) =>
    isDemoMode ? (DEMO_API_LABELS[apiId] || apiId) : API_SOURCES_CONFIG[apiId].name;

  // Build endpoint cards from config
  useEffect(() => {
    const cards: EndpointCard[] = [];
    Object.entries(API_SOURCES_CONFIG).forEach(([apiId, config]) => {
      config.categories.forEach(category => {
        category.endpoints.forEach(endpoint => {
          cards.push({
            api: apiId,
            endpointId: endpoint.id,
            endpointPath: endpoint.path,
            description: endpoint.description,
            category: category.name,
            method: endpoint.method,
            color: API_COLORS[apiId] || '#gray',
            fields: endpoint.fields,
          });
        });
      });
    });
    setEndpoints(cards);
  }, []);

  // Check for match when selections change
  useEffect(() => {
    if (selectedA && selectedB && selectedA.api !== selectedB.api) {
      setShowMatchBanner(true);
      setTimeout(() => setShowMatchBanner(false), 2500);
    }
  }, [selectedA, selectedB]);

  const selectEndpoint = (card: EndpointCard, group: 'A' | 'B') => {
    const selection: ApiSelection = {
      api: card.api,
      endpointId: card.endpointId,
      endpointPath: card.endpointPath,
      description: card.description,
      category: card.category,
      method: card.method,
      fields: card.fields,
    };

    if (group === 'A') {
      setSelectedA(selection);
    } else {
      setSelectedB(selection);
    }
  };

  const generateBusinessCase = async () => {
    if (!selectedA || !selectedB) {
      setError('Please select one API from each group');
      return;
    }

    if (selectedA.api === selectedB.api) {
      setError('Please select APIs from different systems to create a business case');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    const fieldsA = selectedA.fields?.map(f => f.name).join(', ') || 'N/A';
    const fieldsB = selectedB.fields?.map(f => f.name).join(', ') || 'N/A';

    const prompt = `You are a fleet management business analyst. Analyze how combining these two APIs creates business value:

**API A: ${getApiDisplayName(selectedA.api)} - ${selectedA.endpointId}**
Description: ${selectedA.description}
Fields: ${fieldsA}

**API B: ${getApiDisplayName(selectedB.api)} - ${selectedB.endpointId}**
Description: ${selectedB.description}
Fields: ${fieldsB}

Provide a structured business case analysis in this exact JSON format:
{
  "title": "Business Case Title",
  "summary": "One sentence summary of the integration value",
  "dataFromA": ["What data point 1 from API A provides", "What data point 2 provides", "What data point 3 provides"],
  "dataFromB": ["What data point 1 from API B provides", "What data point 2 provides", "What data point 3 provides"],
  "combinedInsight": "Detailed explanation of what becomes possible when A+B are combined",
  "businessSteps": ["Step 1: How data flows", "Step 2: How they connect", "Step 3: What decision/action is enabled"],
  "kpis": ["KPI 1 that can be measured", "KPI 2 that can be measured", "KPI 3 that can be measured"],
  "businessValue": "Quantifiable business impact (cost savings, efficiency gains, etc.)",
  "pythonExample": "A short Python code snippet showing how to combine both APIs (10-15 lines max)"
}

Only respond with valid JSON, no markdown or explanation.`;

    try {
      // II1-E9-S2: server-side proxy holds the Gemini key; the client no
      // longer stores or ships one.
      const response = await authenticatedFetch('/api/ai/gemini/generate', {
        method: 'POST',
        body: JSON.stringify({ model: geminiModel, prompt }),
      });

      // Check if response is ok
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.error('API Error:', response.status, errorText);
        throw new Error(`API Error (${response.status}): ${errorText || 'Request failed'}`);
      }

      const responseText = await response.text();
      if (!responseText) {
        throw new Error('Empty response from API - check your API key');
      }

      const data = JSON.parse(responseText);

      if (data.error) {
        throw new Error(data.error.message);
      }

      const text = data.candidates[0].content.parts[0].text;
      // Extract JSON from response (handle markdown code blocks)
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error('Invalid response format from AI');

      const businessCase: BusinessCase = JSON.parse(jsonMatch[0]);
      setResult(businessCase);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate business case');
    } finally {
      setLoading(false);
    }
  };

  const getColorClasses = (api: string) => {
    const colorMap: Record<string, { bg: string; text: string; border: string }> = {
      aquarius: { bg: 'bg-chart-2/20', text: 'text-chart-2', border: 'border-chart-2' },
      jigsaw: { bg: 'bg-chart-7/20', text: 'text-chart-7', border: 'border-chart-7' },
      optifleet: { bg: 'bg-chart-1/20', text: 'text-chart-1', border: 'border-chart-1' },
      qargo: { bg: 'bg-chart-3/20', text: 'text-chart-3', border: 'border-chart-3' },
    };
    return colorMap[api] || { bg: 'bg-accent/20', text: 'text-muted-foreground', border: 'border-muted-foreground' };
  };

  const renderEndpointCard = (card: EndpointCard, group: 'A' | 'B') => {
    const selected = group === 'A' ? selectedA : selectedB;
    const isSelected = selected?.endpointId === card.endpointId && selected?.api === card.api;
    const colors = getColorClasses(card.api);

    return (
      <div
        key={`${card.api}-${card.endpointId}-${group}`}
        onClick={() => selectEndpoint(card, group)}
        className={`relative p-3 rounded-lg cursor-pointer transition-all border-2 ${
          isSelected
            ? `${colors.border} ${colors.bg}`
            : 'border-border hover:border-success hover:bg-success/5'
        }`}
      >
        {isSelected && (
          <div className="absolute top-2 right-2 w-5 h-5 bg-success rounded-full flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
        <div className="flex items-start gap-3">
          <span className={`w-2 h-2 mt-2 rounded-full ${colors.text} flex-shrink-0`}></span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-white text-sm">{card.endpointId}</span>
              <span className={`text-xs ${colors.bg} ${colors.text} px-1.5 py-0.5 rounded`}>
                {getApiDisplayName(card.api)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{card.description}</p>
          </div>
        </div>
      </div>
    );
  };

  const renderEquationBox = () => {
    const colorsA = selectedA ? getColorClasses(selectedA.api) : null;
    const colorsB = selectedB ? getColorClasses(selectedB.api) : null;
    const hasMatch = selectedA && selectedB && selectedA.api !== selectedB.api;

    return (
      <div className="bg-gradient-to-br from-success/10 to-success/5 border-2 border-success rounded-xl p-6 mb-6">
        <p className="text-sm text-muted-foreground mb-4 text-center">
          Select APIs from different systems to create business logic
        </p>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          {/* Box A */}
          <div className="flex flex-col items-center">
            <div
              className={`w-24 h-24 rounded-xl flex items-center justify-center text-3xl font-bold transition-all ${
                selectedA
                  ? `${colorsA?.border} ${colorsA?.bg} border-2`
                  : 'border-2 border-dashed border-accent bg-secondary text-muted-foreground'
              }`}
            >
              {selectedA ? (
                <span className={`${colorsA?.text} text-lg font-bold`}>
                  {selectedA.endpointId.substring(0, 8)}
                </span>
              ) : (
                'A'
              )}
            </div>
            <span className="text-xs text-muted-foreground mt-2">System A</span>
          </div>

          <span className="text-3xl font-bold text-success">+</span>

          {/* Box B */}
          <div className="flex flex-col items-center">
            <div
              className={`w-24 h-24 rounded-xl flex items-center justify-center text-3xl font-bold transition-all ${
                selectedB
                  ? `${colorsB?.border} ${colorsB?.bg} border-2`
                  : 'border-2 border-dashed border-accent bg-secondary text-muted-foreground'
              }`}
            >
              {selectedB ? (
                <span className={`${colorsB?.text} text-lg font-bold`}>
                  {selectedB.endpointId.substring(0, 8)}
                </span>
              ) : (
                'B'
              )}
            </div>
            <span className="text-xs text-muted-foreground mt-2">System B</span>
          </div>

          <span className="text-3xl font-bold text-success">=</span>

          {/* Box C */}
          <div className="flex flex-col items-center">
            <div
              className={`w-24 h-24 rounded-xl flex items-center justify-center transition-all ${
                hasMatch
                  ? 'border-2 border-success bg-success/20'
                  : 'border-2 border-dashed border-accent bg-secondary'
              }`}
            >
              {hasMatch ? (
                <Lightbulb className="w-10 h-10 text-success" />
              ) : (
                <span className="text-3xl font-bold text-muted-foreground">C</span>
              )}
            </div>
            <span className="text-xs text-muted-foreground mt-2">Insight</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Match Banner */}
      {showMatchBanner && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 bg-gradient-to-r from-success to-primary px-6 py-3 rounded-lg font-semibold text-white z-50 shadow-lg shadow-success/40 animate-bounce">
          🎯 BUSINESS LOGIC UNLOCKED!
        </div>
      )}

      {/* Header */}
      <div className="bg-card border-b border-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Bot className="w-6 h-6 text-chart-5" />
              <h2 className="text-2xl font-bold text-white">API Business Case Explorer</h2>
            </div>
            <p className="text-sm text-muted-foreground mt-1">Combine APIs to discover business insights with AI</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Powered by</span>
            <span className="text-sm font-semibold bg-gradient-to-r from-ring via-success to-warning bg-clip-text text-transparent">
              Google Gemini
            </span>
          </div>
        </div>

        {/* Model Config */}
        <div className="mt-4 flex gap-4 items-end">
          <div className="w-48">
            <label className="text-xs text-muted-foreground mb-1 block">Model</label>
            <select
              value={geminiModel}
              onChange={(e) => setGeminiModel(e.target.value as GeminiModel)}
              className="w-full px-3 py-2 bg-secondary border border-accent rounded-lg text-sm text-white focus:border-success focus:outline-none"
            >
              {GEMINI_MODELS.map((model) => (
                <option key={model.value} value={model.value}>
                  {model.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Equation Display */}
        {renderEquationBox()}

        {/* API Selection */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Group A */}
          <div className="bg-card border border-border rounded-lg p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Select API (A)</h3>
              <span
                className={`text-xs px-2 py-1 rounded ${
                  selectedA ? `${getColorClasses(selectedA.api).bg} ${getColorClasses(selectedA.api).text}` : 'bg-secondary text-muted-foreground'
                }`}
              >
                {selectedA ? '1 selected' : '0 selected'}
              </span>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
              {endpoints.map((card) => renderEndpointCard(card, 'A'))}
            </div>
          </div>

          {/* Group B */}
          <div className="bg-card border border-border rounded-lg p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Select API (B)</h3>
              <span
                className={`text-xs px-2 py-1 rounded ${
                  selectedB ? `${getColorClasses(selectedB.api).bg} ${getColorClasses(selectedB.api).text}` : 'bg-secondary text-muted-foreground'
                }`}
              >
                {selectedB ? '1 selected' : '0 selected'}
              </span>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
              {endpoints.map((card) => renderEndpointCard(card, 'B'))}
            </div>
          </div>
        </div>

        {/* Generate Button */}
        <div className="text-center mb-6">
          <button
            onClick={generateBusinessCase}
            disabled={loading || !selectedA || !selectedB}
            className="px-8 py-3 bg-primary hover:bg-success disabled:bg-accent disabled:cursor-not-allowed text-white rounded-lg font-semibold transition-all hover:shadow-lg hover:shadow-success/30 flex items-center gap-3 mx-auto"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Generating...
              </>
            ) : (
              <>
                <Zap className="w-5 h-5" />
                Generate Business Case with AI
              </>
            )}
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-card border border-destructive/50 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-3 text-destructive mb-2">
              <AlertCircle className="w-6 h-6" />
              <span className="font-semibold">Error</span>
            </div>
            <p className="text-muted-foreground">{error}</p>
            <p className="text-sm text-muted-foreground mt-2">
              Try selecting a different model, or wait a moment if you hit rate limits.
            </p>
          </div>
        )}

        {/* Results Display */}
        {loading && (
          <div className="bg-card border border-border rounded-lg p-8 text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="w-2 h-2 bg-success rounded-full animate-pulse"></span>
              <span className="w-2 h-2 bg-success rounded-full animate-pulse delay-100"></span>
              <span className="w-2 h-2 bg-success rounded-full animate-pulse delay-200"></span>
            </div>
            <p className="text-muted-foreground">AI is analyzing your API combination...</p>
          </div>
        )}

        {result && !loading && (
          <div className="space-y-6 animate-fadeIn">
            {/* Title Card */}
            <div className="bg-gradient-to-br from-success/15 to-success/5 border border-success rounded-xl p-6">
              <h3 className="text-xl font-bold text-white mb-2">{result.title}</h3>
              <p className="text-muted-foreground">{result.summary}</p>
            </div>

            {/* A + B = C Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* A Data */}
              <div className="bg-card border border-border rounded-lg p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`w-8 h-8 rounded-lg ${selectedA && getColorClasses(selectedA.api).bg} ${selectedA && getColorClasses(selectedA.api).text} flex items-center justify-center font-bold`}>
                    A
                  </span>
                <span className="font-semibold text-white">{selectedA && getApiDisplayName(selectedA.api)}</span>
                </div>
                <p className="text-sm text-muted-foreground mb-3">{selectedA?.endpointId}</p>
                <ul className="space-y-2">
                  {result.dataFromA.map((item, i) => (
                    <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className={`mt-1 ${selectedA && getColorClasses(selectedA.api).text}`}>•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* B Data */}
              <div className="bg-card border border-border rounded-lg p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`w-8 h-8 rounded-lg ${selectedB && getColorClasses(selectedB.api).bg} ${selectedB && getColorClasses(selectedB.api).text} flex items-center justify-center font-bold`}>
                    B
                  </span>
                  <span className="font-semibold text-white">{selectedB && API_SOURCES_CONFIG[selectedB.api].name}</span>
                </div>
                <p className="text-sm text-muted-foreground mb-3">{selectedB?.endpointId}</p>
                <ul className="space-y-2">
                  {result.dataFromB.map((item, i) => (
                    <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className={`mt-1 ${selectedB && getColorClasses(selectedB.api).text}`}>•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* C Insight */}
              <div className="bg-card border border-success/50 rounded-lg p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 rounded-lg bg-success/20 text-success flex items-center justify-center font-bold">
                    C
                  </span>
                  <span className="font-semibold text-white">Combined Insight</span>
                </div>
                <p className="text-sm text-muted-foreground">{result.combinedInsight}</p>
              </div>
            </div>

            {/* How It Works */}
            <div className="bg-card border border-border rounded-lg p-5">
              <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-success" />
                How A + B Combine
              </h4>
              <div className="space-y-3">
                {result.businessSteps.map((step, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-success/20 text-success flex items-center justify-center text-sm flex-shrink-0">
                      {i + 1}
                    </span>
                    <p className="text-muted-foreground text-sm">{step}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* KPIs & Business Value */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-card border border-border rounded-lg p-5">
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-success" />
                  Key Performance Indicators
                </h4>
                <div className="flex flex-wrap gap-2">
                  {result.kpis.map((kpi, i) => (
                    <span key={i} className="px-3 py-1.5 bg-secondary rounded-lg text-sm text-muted-foreground">
                      {kpi}
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-5">
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-success" />
                  Business Value
                </h4>
                <p className="text-muted-foreground">{result.businessValue}</p>
              </div>
            </div>

            {/* Python Example */}
            <div className="bg-card border border-border rounded-lg p-5">
              <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                <Code className="w-5 h-5 text-success" />
                Python Integration Example
              </h4>
              <div className="bg-background border-l-4 border-success rounded-r-lg p-4 overflow-x-auto">
                <pre className="text-success text-sm font-mono leading-relaxed">{result.pythonExample}</pre>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
